// SPDX-License-Identifier: AGPL-3.0-or-later

import GameMode from '@app/features/accessibility/state/GameMode';
import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import type {RoomQualitySetting, RoomQualityTier} from '@app/features/voice_scene/utils/RoomQuality';
import {makeAutoObservable} from 'mobx';

export type VoiceSceneFailure = 'context_lost' | 'load_failed';
// Frame cap of the room; 'auto' starts at 60 and steps down to 30 while this device's frames cost too much.
export type VoiceSceneFrameRate = 'auto' | 30 | 60;
// A tier 'auto' fell to is forgotten this long after it last fell (a driver or hardware may have improved since).
const AUTO_QUALITY_CAP_MS = 7 * 24 * 60 * 60 * 1000;

// Per-device choices for the 3D voice room; `failure` lives only for this session.
class VoiceSceneSettingsState {
	preferRoom = true;
	spatialAudio = true;
	// Cameras on avatars inside the room (on by default, like the flat call view).
	showRoomCameras = true;
	// Avatar mode only: draw people as today's discs instead of 3D characters.
	classicView = false;
	// Send where my character looks (pointer over the room) to the others.
	shareHeadDirection = true;
	frameRate: VoiceSceneFrameRate = 'auto';
	quality: RoomQualitySetting = 'auto';
	// 'auto' only: the tier this device fell to while drawing a room; later rooms start there. It only goes down on
	// its own, is forgotten at start-up a week after it last fell (autoQualityCapAt, epoch ms), and choosing a
	// quality clears it.
	autoQualityCap: RoomQualityTier | null = null;
	autoQualityCapAt: number | null = null;
	// Room ambience loudness, 0 to 1; 0 turns the ambience off.
	ambienceVolume = 0.5;
	// Footstep loudness in the room, 0 to 2: 1 plays the steps as made, 2 is +6 dB (the step files peak near -6 dBFS
	// and go through the -8 dB event bus, so they stay clear of clipping); 0 turns them off.
	footstepVolume = 1;
	// How fast the mouse turns the walking camera, 0.1 to 3; 1 is the room's own speed. Touch is not affected.
	mouseSensitivity = 1;
	// Loudness of a video played on the room TV, 0 to 1, for this viewer only.
	tvVideoVolume = 0.5;
	// The room TV's video shows YouTube's subtitles, for this viewer only; off until turned on.
	tvCaptions = false;
	// Others' slaps miss my character: my client never answers them, so nobody sees it hit.
	refuseSlaps = false;
	// The room toolbar's tucked-away controls are open.
	toolbarExpanded = false;
	// The voice channel's messages are open in the room.
	chatOpen = false;
	failure: VoiceSceneFailure | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void this.initPersistence();
	}

	private async initPersistence(): Promise<void> {
		await makePersistent(this, 'VoiceSceneSettings', [
			'preferRoom',
			'spatialAudio',
			'showRoomCameras',
			'classicView',
			'shareHeadDirection',
			'frameRate',
			'quality',
			'autoQualityCap',
			'autoQualityCapAt',
			'ambienceVolume',
			'footstepVolume',
			'mouseSensitivity',
			'tvVideoVolume',
			'tvCaptions',
			'refuseSlaps',
			'toolbarExpanded',
			'chatOpen',
		]);
		this.expireAutoQualityCap(Date.now());
	}

	// Micup: lite mode draws the room on the low tier (30 fps, no idle or ambient motion, no bloom); the
	// chosen quality stays and is back when it ends.
	get effectiveQuality(): RoomQualitySetting {
		return GameMode.lightMode ? 'low' : this.quality;
	}

	// A cap without its time (saved before the time was kept) counts as expired.
	private expireAutoQualityCap(now: number): void {
		if (this.autoQualityCapAt !== null && now - this.autoQualityCapAt < AUTO_QUALITY_CAP_MS) return;
		this.autoQualityCap = null;
		this.autoQualityCapAt = null;
	}

	setPreferRoom(preferRoom: boolean): void {
		this.preferRoom = preferRoom;
	}

	setSpatialAudio(spatialAudio: boolean): void {
		this.spatialAudio = spatialAudio;
	}

	setShowRoomCameras(showRoomCameras: boolean): void {
		this.showRoomCameras = showRoomCameras;
	}

	setClassicView(classicView: boolean): void {
		this.classicView = classicView;
	}

	setShareHeadDirection(shareHeadDirection: boolean): void {
		this.shareHeadDirection = shareHeadDirection;
	}

	setFrameRate(frameRate: VoiceSceneFrameRate): void {
		this.frameRate = frameRate;
	}

	setQuality(quality: RoomQualitySetting): void {
		this.quality = quality;
		this.autoQualityCap = null;
		this.autoQualityCapAt = null;
	}

	// The room could not keep up on 'auto'; a chosen quality stays as chosen, and lite mode's low tier says nothing
	// about this device's own tier.
	lowerAutoQuality(tier: RoomQualityTier): void {
		if (this.quality !== 'auto' || GameMode.lightMode) return;
		this.autoQualityCap = tier;
		this.autoQualityCapAt = Date.now();
	}

	setAmbienceVolume(volume: number): void {
		this.ambienceVolume = Math.min(1, Math.max(0, volume));
	}

	setFootstepVolume(volume: number): void {
		this.footstepVolume = Math.min(2, Math.max(0, volume));
	}

	setMouseSensitivity(sensitivity: number): void {
		this.mouseSensitivity = Math.min(3, Math.max(0.1, sensitivity));
	}

	setTvVideoVolume(volume: number): void {
		this.tvVideoVolume = Math.min(1, Math.max(0, volume));
	}

	setTvCaptions(on: boolean): void {
		this.tvCaptions = on;
	}

	setRefuseSlaps(refuse: boolean): void {
		this.refuseSlaps = refuse;
	}

	setToolbarExpanded(expanded: boolean): void {
		this.toolbarExpanded = expanded;
	}

	setChatOpen(open: boolean): void {
		this.chatOpen = open;
	}

	// The room falls back to the flat call view; the voice connection is not touched.
	reportFailure(failure: VoiceSceneFailure): void {
		this.failure = failure;
	}

	retry(): void {
		this.failure = null;
		this.preferRoom = true;
	}
}

export default new VoiceSceneSettingsState();
